import { useState, useEffect } from "react";
import { 
  Search, 
  Star, 
  X,
  MessageSquare,
  Eye,
  Trash2,
  RefreshCw
} from "lucide-react";
import { reviewsAPI } from "../../services/api";

// Helper function to format date from YYYY-MM-DD to DD/M/YYYY
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export function ReviewsList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: "" });
  const [viewModal, setViewModal] = useState({ show: false, review: null });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchReviews();
  }, [page, limit, ratingFilter]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { page, limit };
      if (ratingFilter !== "all") {
        params.rating = ratingFilter;
      }
      
      const response = await reviewsAPI.getAll(params);
      
      if (response.success) {
        setReviews(response.data || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch reviews");
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter reviews locally for search
  const filteredReviews = reviews.filter(review => {
    const matchesSearch = 
      (review.name && review.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (review.role && review.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (review.text && review.text.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRating = ratingFilter === "all" || review.rating === parseInt(ratingFilter);
    
    return matchesSearch && matchesRating;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setRatingFilter("all");
  };

  // Handle delete
  const handleDelete = (id, name) => {
    setDeleteModal({ show: true, id, name });
  };

  const confirmDelete = async () => {
    try {
      await reviewsAPI.delete(deleteModal.id);
      fetchReviews();
    } catch (err) {
      console.error("Error deleting review:", err);
      // Fallback: remove locally
      setReviews(reviews.filter(review => review.id !== deleteModal.id));
    }
    setDeleteModal({ show: false, id: null, name: "" });
  };

  // Handle view
  const handleView = (review) => {
    setViewModal({ show: true, review });
  };

  const closeViewModal = () => {
    setViewModal({ show: false, review: null });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, id: null, name: "" });
  };

  const getRatingStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Member Reviews</h1>
          <p className="text-gray-600">Manage testimonials and feedback from members</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#C46A1B] mx-auto mb-2" />
            <p className="text-gray-500">Loading reviews...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Member Reviews</h1>
          <p className="text-gray-600">Manage testimonials and feedback from members</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchReviews}
            className="mt-2 px-4 py-2 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Member Reviews</h1>
        <p className="text-gray-600">Manage testimonials and feedback from members</p>
      </div>

      {/* Filters - Simple row with search and dropdown */}
      <div className="bg-white p-4 rounded-lg shadow-sm flex flex-wrap gap-4">
        {/* Search */}
        <div className="flex-1 flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
          <Search className="w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, role, or review..."
            className="bg-transparent border-none outline-none text-sm w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Rating Filter Dropdown */}
        <select 
          className="px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-[#C46A1B] focus:border-transparent outline-none"
          value={ratingFilter}
          onChange={(e) => setRatingFilter(e.target.value)}
        >
          <option value="all">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </select>

        {/* Clear Filters Button - shown only when filters are active */}
        {(searchTerm || ratingFilter !== "all") && (
          <button
            onClick={clearFilters}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition whitespace-nowrap"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600">
        Showing {filteredReviews.length} of {total} reviews
      </div>

      {/* Reviews Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-600">
                <th className="p-4">Member</th>
                <th className="p-4">Role</th>
                <th className="p-4">Rating</th>
                <th className="p-4">Review</th>
                <th className="p-4">Date</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredReviews.map((review) => (
                <tr key={review.id} className="hover:bg-gray-50">
                  <td className="p-4">
                    <p className="font-medium">{review.name}</p>
                  </td>
                  <td className="p-4 text-sm">{review.role}</td>
                  <td className="p-4">{getRatingStars(review.rating)}</td>
                  <td className="p-4">
                    <button
                      onClick={() => handleView(review)}
                      className="text-sm text-left hover:text-[#C46A1B] transition line-clamp-2 max-w-xs"
                    >
                      {review.text}
                    </button>
                  </td>
                  <td className="p-4 text-sm">{formatDate(review.created_at)}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {/* View */}
                      <button
                        onClick={() => handleView(review)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="View"
                      >
                        <Eye className="w-4 h-4 text-blue-600" />
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(review.id, review.name)}
                        className="p-1 hover:bg-red-100 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredReviews.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg">No reviews found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Pagination */}
        <div className="p-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {filteredReviews.length} of {total} reviews
          </p>
          <div className="flex gap-2">
            <button 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button className="px-3 py-1 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]">
              {page}
            </button>
            <button 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* View Review Modal - Full screen overlay with inline styles */}
      {viewModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={closeViewModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#111111]">Review Details</h3>
              <button 
                onClick={closeViewModal}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-[#111111]">{viewModal.review?.name}</h4>
                  <p className="text-sm text-gray-600">{viewModal.review?.role}</p>
                </div>
                <div className="text-right">
                  <div className="flex gap-1 mb-1">
                    {getRatingStars(viewModal.review?.rating)}
                  </div>
                  <p className="text-xs text-gray-500">{formatDate(viewModal.review?.created_at)}</p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">{viewModal.review?.text}</p>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end">
              <button
                onClick={closeViewModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Full screen overlay with inline styles */}
      {deleteModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={closeDeleteModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 text-red-600 mb-4">
                <Trash2 className="w-6 h-6" />
                <h3 className="text-lg font-semibold">Delete Review</h3>
              </div>
              <p className="text-gray-600 mb-2">Are you sure you want to delete this review?</p>
              <p className="text-sm bg-gray-50 p-3 rounded-lg mb-4">"{deleteModal.name}"</p>
              <p className="text-xs text-gray-500 mb-6">This action cannot be undone.</p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Delete Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
